/* ============================================================================
   SERAFINA — cart tier seam, drawn as a seam being sewn.
   Ladder keyed to total cart quantity, backed by the store's live automatic
   discounts: 1 article = spedizione gratuita, 2 = −10%, 3 = −15%, 4 = −20%.

   Two stitches on one hairline:
     solid stitch = what is actually in the cart (never lies),
     faded stitch = what the next add would sew on, led by a needle.
   Both are animated by small springs (the house fabric-physics feel); the
   needle tilts with its own velocity and keeps a hand-stitch bob at rest.
   The message always sells the next step and quotes the money it is worth.

   The seam renders in two places, driven by this one script:
     PDP bar (under add-to-cart)  — previews cart + the selected quantity;
     drawer bar (variant=drawer)  — previews cart + one more item, priced at
                                    the cart average. It sits off-viewport
                                    until the drawer opens, so its entrance
                                    sew-in plays exactly when the drawer
                                    slides in — the moment of the unlock.

   Cart state is shared: the theme's cart events re-read /cart.js and every
   bar re-renders, so PDP and drawer always agree.
   ============================================================================ */
(function () {
  'use strict';

  var TIERS = 4;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var bars = [];
  var cartCount = 0;
  var cartTotal = 0; /* cents, before discounts */
  var moneyParts = ['', ' kr'];

  function money(cents) {
    var n = Math.round(cents / 100);
    var s = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (moneyParts[0] + s + moneyParts[1]).trim();
  }

  function save(cents) {
    return '<span class="sf-tiers__save">' + money(cents) + '</span>';
  }

  /* achieved = solid state only; always sell the following step.
     Static markup only — the money span takes the thread colour. */
  function message(projected, projTotal) {
    if (cartCount >= TIERS) return '−20% sbloccato — stai risparmiando ' + save(cartTotal * 0.20);
    if (projected >= TIERS) return 'Stai per sbloccare il −20%: risparmi ' + save(projTotal * 0.20);
    if (projected === 3) return 'Stai per sbloccare il −15%: risparmi ' + save(projTotal * 0.15) + ' — un altro e hai il −20%';
    if (projected === 2) return 'Stai per sbloccare il −10%: risparmi ' + save(projTotal * 0.10) + ' — un altro e hai il −15%';
    if (projected === 1) return 'Con questo articolo la spedizione è gratuita — un altro e hai il −10%';
    return 'Spedizione gratuita dal primo articolo';
  }

  function makeBar(root) {
    var fill = root.querySelector('[data-sf-tiers-fill]');
    var ghost = root.querySelector('[data-sf-tiers-preview]');
    var needle = root.querySelector('[data-sf-tiers-needle]');
    var track = root.querySelector('.sf-tiers__track');
    var msg = root.querySelector('[data-sf-tiers-message]');
    var steps = root.querySelectorAll('.sf-tiers__step');

    var isDrawer = root.getAttribute('data-variant') === 'drawer';
    var isInline = root.classList.contains('sf-tiers--inline'); /* display:contents — the root has no box */
    var unitPrice = parseInt(root.getAttribute('data-price'), 10) || 0; /* cents */

    /* the PDP quantity stepper; the drawer always previews one more item */
    var qtyInput = null;
    if (!isDrawer) {
      var scope = root.closest('.shopify-section') || document;
      qtyInput = scope.querySelector('.js-qty__num, form[action*="/cart/add"] input[name="quantity"]');
    }

    function selectedQty() {
      if (!qtyInput) return 1;
      var q = parseInt(qtyInput.value, 10);
      return isNaN(q) || q < 0 ? 1 : q;
    }

    /* what the next article costs: the product on a PDP, the cart average
       in the drawer (where "one more" is any product) */
    function nextPrice() {
      if (!isDrawer && unitPrice) return unitPrice;
      if (cartCount > 0) return cartTotal / cartCount;
      return unitPrice;
    }

    /* ---- springs: one for each stitch, driving width + the needle ---- */
    var solidS = { x: 0, v: 0, t: 0, k: 0.14 };
    var ghostS = { x: 0, v: 0, t: 0, k: 0.14 };
    var raf = null;

    function step(s) {
      if (reduced) { s.x = s.t; s.v = 0; return; }
      s.v += (s.t - s.x) * s.k;
      s.v *= 0.82;
      s.x += s.v;
    }

    function settled(s) {
      return Math.abs(s.v) < 0.01 && Math.abs(s.t - s.x) < 0.05;
    }

    function frame() {
      step(solidS);
      step(ghostS);
      fill.style.width = solidS.x + '%';
      ghost.style.width = ghostS.x + '%';
      if (needle) {
        /* the needle rides just ahead of and above the last stitch, as if it
           has pulled up out of the fabric; steeper when moving fast */
        var w = track.clientWidth;
        var tilt = -26 - Math.max(-10, Math.min(10, ghostS.v * 2.2));
        needle.style.transform = 'translate(' + (w * ghostS.x / 100 - 5) + 'px, -10px) rotate(' + tilt + 'deg)';
      }
      if (settled(solidS) && settled(ghostS)) {
        solidS.x = solidS.t; ghostS.x = ghostS.t; raf = null;
        fill.style.width = solidS.x + '%';
        ghost.style.width = ghostS.x + '%';
        /* entrance run is slower; from here on react at hand speed */
        solidS.k = ghostS.k = 0.14;
        root.classList.remove('is-sewing'); /* resume the idle hand-stitch bob */
      } else {
        raf = requestAnimationFrame(frame);
      }
    }

    function nudge() {
      root.classList.add('is-sewing');
      if (!raf) raf = requestAnimationFrame(frame);
    }

    /* ---- render ---- */
    var prevText = '';
    var entered = false; /* the seam sews itself in when first seen */
    function render() {
      if (!entered) return;
      var qty = selectedQty();
      var projected = Math.min(cartCount + qty, TIERS);
      var projTotal = cartTotal + qty * nextPrice();

      solidS.t = Math.min(cartCount, TIERS) / TIERS * 100;
      ghostS.t = projected / TIERS * 100;
      /* the needle leads the preview stitch, then retires once the seam is
         fully threaded (also keeps it inside the column on small screens) */
      root.classList.toggle('has-needle', ghostS.t > solidS.t + 0.5 && ghostS.t < 99.9);
      nudge();

      for (var i = 0; i < steps.length; i++) {
        var el = steps[i];
        var tier = parseInt(el.getAttribute('data-tier'), 10);
        var done = cartCount >= tier;
        var preview = !done && projected >= tier;
        if ((done && !el.classList.contains('is-done')) ||
            (preview && !el.classList.contains('is-preview'))) {
          el.classList.remove('just-lit');
          void el.offsetWidth;
          el.classList.add('just-lit');
        }
        el.classList.toggle('is-done', done);
        el.classList.toggle('is-preview', preview);
      }

      /* the number in the spool takes the thread colour once the selection earns a discount */
      var qwrap = qtyInput && qtyInput.closest('.product__quantity');
      if (qwrap) qwrap.classList.toggle('sf-earning', projected >= 2);

      var text = message(projected, projTotal);
      if (text !== prevText) {
        prevText = text;
        msg.innerHTML = text;
        msg.classList.remove('is-fresh');
        void msg.offsetWidth;
        msg.classList.add('is-fresh');
      }
    }

    /* first view: the needle draws the seam in slowly, so the metaphor is
       seen even with no interaction. The drawer bar sits off-viewport until
       the drawer opens — its entrance plays at the moment of the unlock. */
    function enter() {
      if (entered) return;
      entered = true;
      solidS.k = ghostS.k = 0.05;
      render();
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { io.disconnect(); enter(); }
      }, { threshold: 0.35 });
      io.observe(isInline && track ? track : root);
    } else {
      enter();
    }

    /* the PDP stepper: the theme sets input.value programmatically on +/−
       (no input event off-cart), so watch the buttons and read after the
       handler has run; the input itself covers typing */
    if (qtyInput) {
      qtyInput.addEventListener('input', render);
      qtyInput.addEventListener('change', render);
      var wrapper = qtyInput.closest('.js-qty__wrapper');
      if (wrapper) {
        wrapper.addEventListener('click', function (e) {
          if (!e.target.closest('.js-qty__adjust')) return;
          /* the spool gives a small tug toward the seam as the thread is pulled */
          wrapper.classList.remove('sf-tug'); void wrapper.offsetWidth; wrapper.classList.add('sf-tug');
          setTimeout(render, 0);
        });
      }
    }
    window.addEventListener('resize', nudge);

    return { render: render };
  }

  function renderAll() {
    for (var i = 0; i < bars.length; i++) bars[i].render();
  }

  function setCart(count, totalCents) {
    cartCount = Math.max(0, count | 0);
    if (typeof totalCents === 'number') cartTotal = totalCents;
    renderAll();
  }

  function refresh() {
    var url = (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setCart(cart.item_count || 0, cart.original_total_price || cart.total_price || 0); })
      .catch(function () {});
  }

  function init() {
    var roots = document.querySelectorAll('[data-sf-tiers]');
    if (!roots.length) return;

    /* shared state, server-seeded */
    cartCount = parseInt(roots[0].getAttribute('data-count'), 10) || 0;
    cartTotal = parseInt(roots[0].getAttribute('data-cart-total'), 10) || 0;
    var fmt = (roots[0].getAttribute('data-money') || '{{amount}} kr').match(/^(.*?)\{\{\s*\S+\s*\}\}(.*)$/);
    if (fmt) moneyParts = [fmt[1], fmt[2]];

    for (var i = 0; i < roots.length; i++) bars.push(makeBar(roots[i]));

    /* the theme announces drawer quantity changes with the full cart attached */
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart && typeof e.detail.cart.item_count === 'number') {
        setCart(e.detail.cart.item_count, e.detail.cart.original_total_price || e.detail.cart.total_price);
      } else {
        refresh();
      }
    });
    /* add-to-cart events carry only the added line, so re-read the cart */
    document.addEventListener('ajaxProduct:added', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
